import {
    ACTION_MODAL_CONTENT,
    DELETE_ACTION,
    ERROR,
    FAILED_RUN,
    YES_DELETE_IT,
} from 'Core-utils/constants/constants';
import { useEffect, useState } from 'react';
import { CustomActionWizard } from 'Components/organisms/CustomActionWizard';
import { ViewQueryModal } from 'Components/organisms/ViewQueryModal';
import { AgGridEngagementActions } from 'Components/organisms/AgGridEngagementActions';
import {
    ActionExecutionDto,
    CustomActionDto,
    CustomerActionSummaryDto,
    KIND,
    ServiceProfile,
} from '@ampsec/platform-client';
import { deleteActionById, getActionExecutions, updateActionById } from 'Apis/library';
import { ConfirmationModal } from 'Components/molecules';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_WORKFLOW_ROUTE } from 'Core-utils/routes';
import './styles.scss';
import useTimeSpent from 'Hooks/useTimeSpent';

const COMPONENT_IDENTIFIER = 'Actions Grid';

interface ActionsProps {
    refresh: boolean;
    serviceProfiles: ServiceProfile[];
}

const Actions = ({ refresh, serviceProfiles }: ActionsProps) => {
    useTimeSpent(COMPONENT_IDENTIFIER);
    const navigate = useNavigate();
    const [show, setShow] = useState<boolean>(false);
    const [showViewLogsModal, setShowViewLogsModal] = useState<boolean>(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
    const [refreshData, setRefreshData] = useState<boolean>(refresh);
    const [id, setId] = useState<string>('');
    const [idToDelete, setIdToDelete] = useState<string>('');
    const { data: failedRuns } = useApiObjectData<ActionExecutionDto[], ActionExecutionDto[]>(
        KIND.ACTION_EXECUTION,
        getActionExecutions,
        (a) => a,
        { status: ERROR, actionSpecId: id },
    );

    useEffect(() => {
        setRefreshData((current) => !current);
    }, [refresh]);

    const handleDeleteAction = (row: CustomerActionSummaryDto) => {
        setShowConfirmationModal(true);
        setIdToDelete(row.id);
    };

    const handleDeleteClick = () => {
        deleteActionById(idToDelete).then(() => setRefreshData((current) => !current));
        setShowConfirmationModal(false);
    };

    const handleCloseModal = () => {
        setShowConfirmationModal(false);
    };

    const handleNavigateToExecutions = (row: CustomerActionSummaryDto) => {
        navigate(`${DEFAULT_WORKFLOW_ROUTE}/actions/${row.id}`, {
            state: { displayValue: row.displayValue },
        });
    };

    const handleEditAction = (row: CustomerActionSummaryDto) => {
        setId(row.id);
        setShow(true);
    };

    const handleViewFailedRuns = (row: CustomerActionSummaryDto) => {
        setId(row.id);
        setShowViewLogsModal(true);
    };

    const handleCloseViewQuerModal = () => {
        setShowViewLogsModal(false);
    };

    const handleClose = () => {
        setId('');
        setShow(false);
    };

    const handleActionsSave = async (action: CustomActionDto) => {
        if (action.id && !action.isTemplate) {
            updateActionById(action).then(() => setRefreshData((current) => !current));
        }
        handleClose();
    };

    return (
        <>
            <AgGridEngagementActions
                serviceProfiles={serviceProfiles}
                refreshToken={refreshData}
                onEdit={handleEditAction}
                onDelete={handleDeleteAction}
                onExecution={handleNavigateToExecutions}
                onViewFailedRuns={handleViewFailedRuns}
            />
            <CustomActionWizard show={show} handleSave={handleActionsSave} onClose={handleClose} id={id} />
            <ConfirmationModal
                modalTitle={DELETE_ACTION}
                modalBodyTitle={ACTION_MODAL_CONTENT}
                onPrimaryButtonClick={handleDeleteClick}
                primaryButtonLabel={YES_DELETE_IT}
                show={showConfirmationModal}
                onClose={handleCloseModal}
                buttonVariant="danger"
            />
            <ViewQueryModal
                onClose={handleCloseViewQuerModal}
                show={showViewLogsModal}
                json={(Array.isArray(failedRuns) ? failedRuns : []).map((item) => item.output)}
                isFooter={true}
                title={FAILED_RUN}
            />
        </>
    );
};

export default Actions;
